// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// FAV Inbox (#630) — pure helpers for the left pane's Inbox tab and the dimmed grid
// tiles. Pure so the tests import exactly what the components run.
// Plan: docs/fav-inbox.md. Server side: convex/scheduler/favInbox.ts.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * The FAV Inbox (tabs, dimmed bookings, FAV 2.0 ghosts, Unscheduled, Why?, red boxes) is shown
 * only to users whose User record has inboxFlag === true (Leland 2026-10-16: "Brandon & I are true,
 * everyone else = false … no tabs or Inbox until we are ready to train users on it"). On EVERY
 * build, PROD included (Leland 2026-10-24: FAV 2.0 goes live for chosen offices/users — "Still
 * gated. Same user settings so that only Brandon and I see fav v2 stuff"). Was: never on PROD.
 * Needs Convex. `appEnv` is accepted but no longer decides anything.
 */
// eslint-disable-next-line no-unused-vars
export function favInboxEnabled({ appEnv, convexConfigured, inboxFlag, liveOffice = false }) {
  return Boolean(convexConfigured) && (inboxFlag === true || liveOffice === true);
}

/**
 * FAV 2.0 PROD go-live (Leland 2026-10-28: "tomorrow at 7am edt we go live with FAV v2.0 with those
 * selected 6 offices. All users of these offices will need to see inbox, red boxes, yellow dashed
 * boxes, Why text, but not the replay button"). From `fromMs`, EVERYONE viewing one of these offices
 * gets the FAV 2.0 screens; the ↻ button, hard-surface answers and the grey "would book" previews
 * stay with their own permissions. Same offices + time as the blackout-dates Lambda's
 * FAV2_BOOK_OFFICES / FAV2_BOOK_FROM (10-28-2026-02) — change both together.
 */
const WAVE1 = Date.parse("2026-10-29T07:00:00-04:00");
/**
 * Brandon #892: the second wave goes live on 11/5 at 8:00 am LOCAL time for each office, so each
 * office carries its own go-live time (office → epoch ms). Same offices + times as the Lambda's
 * FAV2_BOOK_OFFICES entries ("MAC@2026-11-05T08:00:00-05:00", 11-04-2026-04) — change both together.
 */
export const FAV2_LIVE = {
  offices: {
    ATL: WAVE1, HUN: WAVE1, DMS: WAVE1, DTW: WAVE1, LAS: WAVE1, OCO: WAVE1,
    MAC: Date.parse("2026-11-05T08:00:00-05:00"), // Georgia — Eastern
    SBN: Date.parse("2026-11-05T08:00:00-05:00"), // South Bend IN — Eastern
    CTS: Date.parse("2026-11-05T08:00:00-05:00"), // Michigan — Eastern
    BHM: Date.parse("2026-11-05T08:00:00-06:00"), // Alabama — Central
    TUS: Date.parse("2026-11-05T08:00:00-07:00"), // Arizona — MST, no daylight saving
    RVS: Date.parse("2026-11-05T08:00:00-08:00"), // California — Pacific
  },
};
export function fav2LiveOffice(location, nowMs = Date.now(), live = FAV2_LIVE) {
  const from = live.offices[String(location || "").trim().toUpperCase()];
  return Number.isFinite(from) && nowMs >= from;
}

/** The job's id as the Inbox stores it (ActiveQueue _id hex, lower-case). */
export function jobKey(project) {
  return String(project?.mongoId || project?.id || "").toLowerCase();
}

/**
 * Leland 2026-10-26: FAV 2.0 for HARD surfaces (vinyl, wood, tile, backsplash, plank — the
 * every-job answers: grey suggestion boxes and red boxes) is a separate permission, the
 * "favHardSurface" token in the user's screenAccessList. Without it a user sees FAV 2.0 for
 * soft surface (carpet) only. Leland + Brandon have both.
 */
export const FAV_HARD_SURFACE_TOKEN = "favHardSurface";

/**
 * #787 (Leland 2026-10-27): the red box's Re-run button — "for Brandon and I" — is shown only to
 * users with the "favRerun" token in their screenAccessList (the server checks it too).
 */
export const FAV_RERUN_TOKEN = "favRerun";
export function canRerunFav2(user) {
  return Array.isArray(user?.screenAccessList) && user.screenAccessList.includes(FAV_RERUN_TOKEN);
}
export function canSeeFavHardSurface(user) {
  return Array.isArray(user?.screenAccessList) && user.screenAccessList.includes(FAV_HARD_SURFACE_TOKEN);
}
/** Drops hard-surface answers unless the user may see them. */
export function withoutHardSurface(rows, showHard) {
  const list = Array.isArray(rows) ? rows : [];
  return showHard ? list : list.filter((r) => !(r && r.hardSurface === true));
}

/**
 * #755: FAV 2.0's "couldn't schedule" answers by job id (lower-cased, like jobKey) — the tiles
 * with a red box and an ⓘ. rows: scheduler/fav2Log:skipsForLocation.
 */
export function buildSkipsByJob(rows) {
  const m = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    const id = String((r && r.jobId) || "").toLowerCase();
    if (id && r.why) m.set(id, r);
  }
  return m;
}

/**
 * Notifications "Clear all" (Leland 2026-10-26): every message on screen — the open bookings
 * (Assigned, Scheduled). Customer-cancelled and Unscheduled rows aren't shown any more (#772,
 * Brandon 2026-10-26), so Clear all never touches them. Capped like the server.
 */
export function clearableIds(rows, max = 200) {
  const ids = (Array.isArray(rows) ? rows : [])
    .map((r) => r && r.id)
    .filter(Boolean);
  return [...new Set(ids)].slice(0, max);
}

/** Set of job ids with an open (pending or deferred) Inbox row — the tiles to dim. */
export function openJobIds(rows) {
  return new Set((Array.isArray(rows) ? rows : []).map((r) => String(r.jobId || "").toLowerCase()).filter(Boolean));
}

/**
 * #787 (Leland 2026-10-27): the open bookings FAV 2.0 made WITH a crew. On the grid such a job is
 * drawn once, as itself, in the yellow-dashed FAV 2.0 style — not greyed out, and no separate
 * "would book" ghost (buildGhostsByCell skips them).
 */
export function fav2CrewedJobIds(rows) {
  return new Set(
    (Array.isArray(rows) ? rows : [])
      .filter((r) => String(r.bookedInstaller ?? "").trim() !== "")
      .map((r) => String(r.jobId || "").toLowerCase())
      .filter(Boolean)
  );
}

/**
 * Reassign is offered only when FAV assigned a crew AND that crew is still on the job
 * (Leland: "only shown if a crew was auto-assigned"). FAV never assigns one today.
 */
export function canReassign(row, project) {
  const booked = String(row?.bookedInstaller ?? "").trim();
  return booked !== "" && String(project?.installer ?? "").trim() === booked;
}

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "2026-11-10" → "11/10/2026" (the grid's date format), else "". */
export function isoToMdy(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${+m[2]}/${+m[3]}/${m[1]}` : "";
}

/**
 * The drop target for each button — the same ids the grid's own cells use, so the
 * button runs exactly the drag-drop path (same windows, same FileMaker update):
 *   Unschedule → "Unassigned_RTS" (the RTS pane)
 *   Reassign   → "Unassigned_<Day>_<M/D/YYYY>" (the Unassigned row on the booked date)
 */
export const UNSCHEDULE_DROP_ID = "Unassigned_RTS";
export function reassignDropId(bookedDateIso) {
  const mdy = isoToMdy(bookedDateIso);
  if (!mdy) return "";
  const [y, m, d] = bookedDateIso.split("-").map(Number);
  return `Unassigned_${DAY_ABBR[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}_${mdy}`;
}

/**
 * "Mod" (Leland 2026-10-26): the drop id of the job's OWN grid cell, so the Inbox can make the
 * same three calls a real drag onto that cell makes and the app opens its usual scheduling
 * window (est. completion date + arrival window + Save). Crew cell "<payroll>_<weekday 0=Mon>_<M/D/YYYY>"
 * (OptimizerGrid), or "Unassigned_<Day>_<M/D/YYYY>" when the job has no crew. "" when the job
 * has no date.
 */
export function modDropId(project) {
  const raw = String(project?.start || "").trim();
  let y, m, d;
  let mm = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (mm) [y, m, d] = [+mm[1], +mm[2], +mm[3]];
  else if ((mm = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) [m, d, y] = [+mm[1], +mm[2], +mm[3]];
  else return "";
  const mdy = `${m}/${d}/${y}`;
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const installer = String(project?.installer || "").trim();
  return installer ? `${installer}_${(dow + 6) % 7}_${mdy}` : `Unassigned_${DAY_ABBR[dow]}_${mdy}`;
}

/** The job's CURRENT date (its grid tile's start, M/D/YYYY or ISO) as ISO, else "". */
export function projectStartIso(project) {
  const raw = String(project?.start || "").trim();
  let mm = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (mm) return `${mm[1]}-${mm[2]}-${mm[3]}`;
  mm = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return mm ? `${mm[3]}-${mm[1].padStart(2, "0")}-${mm[2].padStart(2, "0")}` : "";
}

/**
 * The Inbox's three accordions (Leland 2026-10-13), top to bottom:
 *   Assigned   — FAV booked a crew (FAV v2; empty today)
 *   Unassigned — FAV booked the day only (every booking today)
 *   Deferred   — seen, still unapproved
 */
export function groupInbox(rows) {
  // Notifications (Brandon via #772, 2026-10-26): two sections only — Assigned (FAV booked a
  // crew) and Scheduled (a date, no crew; was "Unassigned"). Deferral no longer exists, so an
  // older deferred booking is listed with the others (it still dims its tile and can be Cleared).
  const list = (Array.isArray(rows) ? rows : []).filter((r) => r.status === "pending" || r.status === "deferred");
  return {
    assigned: list.filter((r) => String(r.bookedInstaller ?? "").trim() !== ""),
    scheduled: list.filter((r) => String(r.bookedInstaller ?? "").trim() === ""),
  };
}

/**
 * The Inbox list (Leland 2026-10-26): ONE list, no sections. UNTIL GO-LIVE it shows every open
 * FAV booking (FAV 1.0's crew-less ones too, where FAV 2.0 says what it would have booked) so
 * Brandon can demo FAV 2.0; customer cancellations are not shown. At go-live it narrows to
 * FAV 2.0's bookings WITH a crew: return groupInbox(...).assigned instead.
 */
export function inboxListRows(rows, { liveOffice = false } = {}) {
  const open = (Array.isArray(rows) ? rows : []).filter((r) => r && (r.status === "pending" || r.status === "deferred"));
  // Go-live (FAV2_LIVE): at a live office the Inbox is FAV 2.0's bookings WITH a crew only.
  return liveOffice ? open.filter((r) => String(r.bookedInstaller ?? "").trim() !== "") : open;
}

/** "2026-11-10" → a local Date on that day (noon, so no DST/UTC edge), else null. */
export function isoToLocalDate(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], 12) : null;
}

/**
 * The Monday (local 00:00) of the Mon–Sun grid week that CONTAINS the date. Not
 * weekDates.getMondayOf, which sends a Sunday to the FOLLOWING Monday — a Sunday
 * booking must jump to the week it's shown in.
 */
export function gridWeekMondayOf(iso) {
  const d = isoToLocalDate(iso);
  if (!d) return null;
  const back = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - back);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

/**
 * Where to put the row's drop-down menu: below the ▾ button if it fits, otherwise
 * above it, and always fully inside the window (a menu hanging off the bottom of the
 * screen needs a scroll to reach — and a scroll closes it). Pure; `btn` is a
 * DOMRect-like {top, bottom, left}, `menu` {width, height}, `view` {width, height}.
 */
export function menuPosition(btn, menu, view, gap = 2, margin = 4) {
  const below = btn.bottom + gap;
  const above = btn.top - gap - menu.height;
  let top = below + menu.height <= view.height - margin ? below : above;
  top = Math.max(margin, Math.min(top, view.height - margin - menu.height));
  const left = Math.max(margin, Math.min(btn.left, view.width - margin - menu.width));
  return { top, left };
}

/**
 * The FAV 2.0 advice line (#642), wording agreed in PRED-028 / fav-2-contracts.md §2:
 * "FAV 2.0 would book: <crew>, <start>–<end>". Null unless the advice is "ok".
 */
export function fav2Line(why) {
  const f = why && why.fav2;
  // PRED-035: a "no suggestion" answer shows FAV 2.0's own first sentence (its why text),
  // e.g. "FAV 2.0 found nothing it could book: no crew at the office has every skill …".
  // Older skips stored no text → nothing shown (no made-up wording).
  if (f && f.status === "skipped") {
    const first = String(f.why || "").split("\n")[0].trim();
    return first || null;
  }
  if (!f || f.status !== "ok") return null;
  const md = (iso) => {
    const m = String(iso || "").match(/^\d{4}-(\d{2})-(\d{2})$/);
    return m ? `${+m[1]}/${+m[2]}` : "";
  };
  const crew = f.installerName || f.installer;
  const when = f.startDateISO === f.endDateISO ? md(f.startDateISO) : `${md(f.startDateISO)}–${md(f.endDateISO)}`;
  return `FAV 2.0 would book: ${crew}, ${when}`;
}

// - - - - - - - - - - Ghost bookings (#642, Leland 2026-10-13: "so Brandon can view,
// watch, get excited") — for each OPEN Inbox booking with FAV 2.0 advice, a faint
// "ghost" card in the advised crew's row on each advised working day. UAT-only, like
// the rest of the Inbox.

/** The advised working days (ISO). Older advice has no list: start → end, Sundays skipped. */
export function ghostDays(fav2) {
  if (!fav2 || fav2.status !== "ok") return [];
  if (Array.isArray(fav2.runDatesISO) && fav2.runDatesISO.length) return fav2.runDatesISO;
  const start = isoToLocalDate(fav2.startDateISO);
  const end = isoToLocalDate(fav2.endDateISO || fav2.startDateISO);
  if (!start || !end) return [];
  const out = [];
  for (const d = new Date(start); d <= end && out.length < 10; d.setDate(d.getDate() + 1)) {
    if (d.getDay() === 0) continue;
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  }
  return out;
}

/** Key for a grid cell: the crew's 7-character payroll + the grid's M/D/YYYY date. */
export const ghostCellKey = (installer, mdy) => `${installer}|${mdy}`;

/**
 * An advice-only proposal (fav2Log, FileMaker's reserve:false call — the job is NOT booked)
 * in the shape buildGhostsByCell reads (#652). TEMPORARY with the "Unscheduled" accordion.
 */
/**
 * The Inbox row's 👤 "worth a person's check" (PRED-032, Leland 2026-10-14: the Inbox shows
 * ONLY FAV 2.0). A row with a FAV 2.0 answer uses FAV 2.0's own flag + reasons (e.g.
 * long_wait); only rows from before FAV 2.0 fall back to the older Jev plan's flag.
 */
export function inboxNeedsPerson(why) {
  if (!why) return { flag: false, reasons: [] };
  if (why.fav2 && typeof why.fav2 === "object") {
    // PRED-035: "FAV 2.0 found no crew it could book" is the strongest reason for a person
    // to look — any skip except degraded data flags the row, with the skip reason.
    if (why.fav2.status === "skipped") {
      const reason = String(why.fav2.reason || "");
      return reason && reason !== "degraded" ? { flag: true, reasons: [reason] } : { flag: false, reasons: [] };
    }
    return { flag: why.fav2.needsPerson === true, reasons: Array.isArray(why.fav2.reasons) ? why.fav2.reasons : [] };
  }
  return { flag: why.needsPerson === true, reasons: Array.isArray(why.reasons) ? why.reasons : [] };
}

export function adviceAsInboxRow(a) {
  return {
    id: `advice-${a.id}`,
    jobId: a.jobId,
    kellerId: a.kellerId,
    status: "advice",
    adviceOnly: true,
    bookedDate: null,
    why: {
      fav2: {
        status: "ok",
        installer: a.installer,
        installerName: a.installerName,
        startDateISO: a.startDateISO,
        endDateISO: a.endDateISO,
        runDatesISO: a.runDatesISO,
        daysConfidence: a.daysConfidence,
        dayKind: a.dayKind,
        why: a.why,
        needsPerson: a.needsPerson === true,
        reasons: Array.isArray(a.reasons) ? a.reasons : [],
      },
    },
  };
}

/** Map cellKey → ghosts, from the open Inbox rows that carry ok FAV 2.0 advice. */
export function buildGhostsByCell(rows) {
  const map = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    const f = r && r.why && r.why.fav2;
    if (!f || f.status !== "ok" || !f.installer) continue;
    // #787 (Leland 2026-10-27): a booking that already HAS a crew (FAV 2.0 booked it) is on the
    // grid itself — its "would book" ghost would only draw the same job a second time.
    if (r.bookedInstaller) continue;
    const days = ghostDays(f);
    days.forEach((iso, i) => {
      const key = ghostCellKey(f.installer, isoToMdy(iso));
      const ghost = {
        jobId: r.jobId,
        rowId: r.id,
        day: i + 1,
        of: days.length,
        crew: f.installerName || f.installer,
        confidence: f.daysConfidence ?? null,
        bookedDate: r.bookedDate,
        adviceOnly: r.adviceOnly === true, // not booked at all (#652)
      };
      map.set(key, [...(map.get(key) || []), ghost]);
    });
  }
  return map;
}
